import type { WatchQueryFetchPolicy } from '@apollo/client';
import { useEffect } from 'react';
import { graphql } from 'react-relay';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { useQueryInterop } from '@confluence/apollo-relay-interop';
import { usePageContentId } from '@confluence/page-context';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData';

import type {
	ConfluenceContentAccessRequestStatus as ConfluenceContentAccessRequestStatusType,
	useAccessRequestsQuery$data,
	useAccessRequestsQuery$variables,
} from './__generated__/useAccessRequestsQuery.graphql';

export const ConfluenceContentAccessRequestStatus = {
	PENDING: 'PENDING' as ConfluenceContentAccessRequestStatusType,
	APPROVE: 'APPROVE' as ConfluenceContentAccessRequestStatusType,
	DENY: 'DENY' as ConfluenceContentAccessRequestStatusType,
	PENDING_SITE_APPROVAL: 'PENDING_SITE_APPROVAL' as ConfluenceContentAccessRequestStatusType,
};

// This is its own hook primarily to cache the results on USD load
// and ensure the Requests button shows as early as possible
export const useAccessRequests = (
	fetchPolicy: WatchQueryFetchPolicy,
	source: string,
	skipOverride: boolean = false,
	status: keyof typeof ConfluenceContentAccessRequestStatus = 'PENDING',
) => {
	const [contentId = ''] = usePageContentId();
	const { cloudId, edition } = useSessionData();
	const skip = !contentId || edition === 'FREE' || skipOverride;

	const { data, loading, error, fetchMore } = useQueryInterop<
		useAccessRequestsQuery$data,
		useAccessRequestsQuery$variables
	>(
		graphql`
			query useAccessRequestsQuery(
				$after: String
				$cloudId: ID!
				$contentId: ID!
				$first: Int = 10
				$status: ConfluenceContentAccessRequestStatus = PENDING
			) {
				confluence_contentAccessRequestByStatus(
					after: $after
					cloudId: $cloudId
					contentId: $contentId
					first: $first
					status: $status
				) {
					nodes {
						user {
							displayName
							emails {
								isPrimary
								value
							}
							photos {
								isPrimary
								value
							}
						}
						accessRequestedAaid
						requestAccessType
						status
						id
					}
					pageInfo {
						endCursor
						hasNextPage
						startCursor
						__typename
					}
				}
			}
		`,
		{
			variables: {
				cloudId,
				contentId,
				status: ConfluenceContentAccessRequestStatus[status],
			},
			skip,
			fetchPolicy,
			notifyOnNetworkStatusChange: true,
		},
	);

	const requests = data?.confluence_contentAccessRequestByStatus?.nodes ?? [];
	const pageInfo = data?.confluence_contentAccessRequestByStatus?.pageInfo;
	const endCursor = pageInfo?.endCursor ?? null;
	const hasNextPage = pageInfo?.hasNextPage ?? false;

	const { createAnalyticsEvent } = useAnalyticsEvents();
	useEffect(() => {
		if (error) {
			createAnalyticsEvent({
				type: 'sendOperationalEvent',
				data: {
					action: 'failed',
					actionSubject: 'accessRequestsQuery',
					source,
					attributes: {
						contentId,
						errorMessage: error.message,
					},
				},
			}).fire();
		}
	}, [error, source, contentId, createAnalyticsEvent]);

	return {
		requests,
		endCursor,
		hasNextPage,
		loading,
		error,
		fetchMore,
		contentId,
		hasFullyLoaded: !!data && !loading,
	};
};
